import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, RouterEvent, Event as NavigationEvent } from '@angular/router';
import { Store } from '@ngxs/store';
import { find, isEqual, some } from 'lodash-es';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { filter, distinctUntilChanged, switchMap, map, skipWhile } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NimbusLicense } from '@nimbus-global/features/shared/services/domain/models/license.model';
import { FeaturePermissionState } from '@nimbus-library/features/feature-permission/store/feature-permission.state';
import { MenuEntryViewModel } from '@nimbus-library/features/menu/models/menu-entry.view-model';
import { MenuViewModel } from '@nimbus-library/features/menu/models/menu.view-model';
import { MenuActions } from '@nimbus-library/features/menu/state/menu.actions';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { IEnvironmentService } from '@nimbus-library/features/configuration/i-environment.service';
import { ChartTheme } from '@nimbus-library/features/charts/models/char-theme.enum';
import { FeatureFlippingService } from '@nimbus-global/features/feature-flipping/feature-flipping.service';
import { MenuEntryType } from '@nimbus-library/features/menu/models/menu-entry.model';
import { computeMenuOverflow, MENU_ITEM_HEIGHT } from '@nimbus-library/features/menu/models/menu-overflow';
import { MenuState } from '../../state/menu.state';

interface IMenuOverflowState {
  entries: MenuEntryViewModel[];
  entryNames: Set<string>;
  top: number;
  active: boolean;
}

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-menu',
  templateUrl: './nimbus-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusMenuComponent implements OnInit, OnDestroy {
  private entries: MenuEntryViewModel[] = [];
  private menuBodyElement: HTMLElement | undefined;
  private readonly resizeObserver = new ResizeObserver(() => this.updateOverflow());

  public menu$: Observable<MenuViewModel>;
  public isApplicationAdmin: boolean;
  public isApplicationPortal: boolean;
  public hasPermissionNotification$: Observable<boolean>;
  public hasFeaturePermissionSet$: Observable<boolean>;
  public hasCcLicense$: Observable<boolean>;
  public url: string;
  public readonly TranslationKeys = TranslationKeys;
  public readonly Icons = ICONS;
  public readonly ChartTheme = ChartTheme;
  public readonly MenuEntryType = MenuEntryType;
  public readonly overflow$ = new BehaviorSubject<IMenuOverflowState>({ entries: [], entryNames: new Set(), top: 0, active: false });

  @ViewChild('menuBody')
  public set menuBody(menuBody: ElementRef<HTMLElement> | undefined) {
    this.resizeObserver.disconnect();
    this.menuBodyElement = menuBody?.nativeElement;

    if (this.menuBodyElement) {
      // The list is observed too: its height changes when entries get filtered in or sub-entries expand, while the body's does not.
      this.resizeObserver.observe(this.menuBodyElement);
      this.resizeObserver.observe(this.menuBodyElement.querySelector('ul'));
    }
  }

  public constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly environmentService: IEnvironmentService,
    private readonly permissionManagerService: IPermissionManagerService,
    protected readonly featureFlippingService: FeatureFlippingService,
  ) {}

  public ngOnInit(): void {
    this.menu$ = this.store.select(MenuState.menu);
    this.isApplicationAdmin = this.environmentService.isApplicationAdmin;
    this.isApplicationPortal = this.environmentService.isApplicationPortal;

    this.hasCcLicense$ = this.permissionManagerService.getPermissionsModel$().pipe(
      skipWhile((permissionModel) => !permissionModel),
      map((permissionModel) => (permissionModel ? permissionModel.hasLicense(NimbusLicense.ContactCenter) : false)),
    );

    this.hasFeaturePermissionSet$ = this.store.select(FeaturePermissionState.featureCollections).pipe(
      distinctUntilChanged((oldValue, newValue) => isEqual(oldValue, newValue)),
      map((featureCollections) => !!featureCollections && featureCollections.length !== 0),
    );

    this.hasPermissionNotification$ = combineLatest([
      this.store
        .select(FeaturePermissionState.featureCollections)
        .pipe(distinctUntilChanged((oldValue, newValue) => isEqual(oldValue, newValue))),
      this.store
        .select(FeaturePermissionState.licenseCollections)
        .pipe(distinctUntilChanged((oldValue, newValue) => isEqual(oldValue, newValue))),
    ]).pipe(
      switchMap(([featureCollections, licenseCollections]) => {
        const feature =
          !featureCollections || featureCollections.length === 0
            ? of([true])
            : combineLatest(featureCollections.map((featureCollection) => featureCollection.allUserPermissionGranted$));

        const license =
          !licenseCollections || licenseCollections.length === 0
            ? of([true])
            : combineLatest(licenseCollections.map((licenseCollection) => licenseCollection.allApplied$));

        return combineLatest([feature, license]);
      }),
      map(([featureResults, licenseResults]) => featureResults.some((result) => !result) || licenseResults.some((result) => !result)),
    );

    this.menu$.pipe(untilDestroyed(this)).subscribe((menu) => (this.entries = menu.entries));

    combineLatest([this.menu$, this.router.events.pipe(filter((e) => e instanceof NavigationStart))])
      .pipe(untilDestroyed(this))
      .subscribe(([menu, e]) => this.storeUrl([...menu.entries, ...menu.footerEntries], e as NavigationStart));

    this.router.events
      .pipe(
        filter((e: NavigationEvent | RouterEvent) => e instanceof NavigationEnd),
        untilDestroyed(this),
      )
      .subscribe((event: RouterEvent) => {
        this.url = event.url;
        requestAnimationFrame(() => this.updateOverflow());
      });
  }

  public ngOnDestroy(): void {
    this.resizeObserver.disconnect();
  }

  public storeUrl(entries: Array<MenuEntryViewModel>, navigationEvent: NavigationStart): void {
    const parent = find(
      entries,
      (entry) => entry.subEntries && some(entry.subEntries, (subEntry) => subEntry.path === navigationEvent.url),
    );

    if (parent) {
      this.store.dispatch(new MenuActions.StoreUrl(parent.name, navigationEvent.url));
    }
  }

  public navigateToExternalUrl(entry: MenuEntryViewModel): void {
    if (entry.path) {
      window.open(entry.path);
    }
  }

  public openUserPreferencesPage(): Promise<boolean> {
    return this.router.navigate(['/user-preferences'], { queryParamsHandling: 'preserve' });
  }

  private updateOverflow(): void {
    if (!this.menuBodyElement) {
      return;
    }

    const items = Array.from(this.menuBodyElement.querySelectorAll<HTMLElement>(':scope > ul > li'));
    const { firstOverflowedIndex, overflowItemTop } = computeMenuOverflow(
      items.map((item) => ({ top: item.offsetTop, height: item.offsetHeight })),
      this.menuBodyElement.clientHeight,
      MENU_ITEM_HEIGHT,
    );
    const overflowedItems = firstOverflowedIndex === null ? [] : items.slice(firstOverflowedIndex);
    const overflowedNames = new Set(overflowedItems.map((item) => item.dataset.menuEntry));

    const current = this.overflow$.value;
    const entries = this.entries.filter((entry) => overflowedNames.has(entry.name));
    const active = overflowedItems.some((item) => !!item.querySelector('.menu-item-active'));

    const entriesChanged = current.entries.length !== entries.length || current.entries.some((entry, index) => entry !== entries[index]);

    if (current.top !== overflowItemTop || current.active !== active || entriesChanged) {
      this.overflow$.next({ entries, entryNames: new Set(entries.map((entry) => entry.name)), top: overflowItemTop, active });
    }
  }
}
